import { useRef, useState } from 'react';

import {
  type ImportTransfersResultDto,
  useImportTransfers,
} from '@/api/transfer/use-import-transfers.ts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';

type ImportTransfersModalProps = {
  onClose: () => void;
};

const ImportTransfersModal = ({ onClose }: ImportTransfersModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportTransfersResultDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: importTransfers, isPending, error } = useImportTransfers();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleImport = () => {
    if (!file) {
      return;
    }

    importTransfers(file)
      .then((data) => {
        setResult(data);
        setFile(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      })
      .catch(() => undefined);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Import Transfers</DialogTitle>
          <DialogDescription>
            Upload a bank statement XML file (camt.053). Transfers already imported (matched by
            reference) are skipped automatically.
          </DialogDescription>
        </DialogHeader>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,text/xml,application/xml"
          onChange={(event) => setFile(event.target.files?.[0] || null)}
          className="text-sm"
        />
        {error && <p className="text-xs text-destructive">Import failed. Please check the file.</p>}
        {result && (
          <div className="text-sm border rounded-lg p-3 flex flex-col gap-1">
            <span>Total entries: {result.total}</span>
            <span className="text-green-600">Imported: {result.imported}</span>
            <span className="text-muted-foreground">Skipped: {result.skipped}</span>
          </div>
        )}
        <DialogFooter>
          <Button onClick={() => onClose()} variant="outline">
            Close
          </Button>
          <Button disabled={!file || isPending} onClick={handleImport}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportTransfersModal;
